import { useState } from 'react';
import { formatDate, formatMoney, formatTime, t } from '@aquabox/shared';
import { useReceipt, useTenant } from '../api/queries';
import { api } from '../api/client';
import { Icon } from '../components/Icon';

/**
 * Makbuz modalı (§3.18). `GET /api/receipts/:id` tek makbuzu döner; panel hiçbir para hesabı
 * yapmaz (kural 4) — tutarlar sunucudan geldiği gibi biçimlenir.
 */
export function ReceiptModal({ receiptId, onClose }: { receiptId: string; onClose: () => void }) {
  const receipt = useReceipt(receiptId);
  const tenant = useTenant();
  const r = receipt.data;

  const lines =
    r?.order && r.order.items.length > 0
      ? r.order.items.map((i) => ({
          label: `${i.qty}× ${i.product.name}`,
          // Satır tutarını sunucudan geldiği gibi göster (kural 4). `Number(unitPrice) * qty`
          // float çarpımı kullanılmaz; `lineTotal` zaten `Prisma.Decimal` ile yazıldı.
          amount: i.lineTotal,
        }))
      : r
        ? [{ label: t(`receiptKind.${r.kind}` as Parameters<typeof t>[0]), amount: r.amount }]
        : [];

  const payment = r?.delivery?.paymentType
    ? t(`paymentType.${r.delivery.paymentType}` as Parameters<typeof t>[0])
    : r
      ? t(`receiptKind.${r.kind}` as Parameters<typeof t>[0])
      : '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('cash.tabReceipts')}
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card modal-card" style={{ '--modal-w': '420px' } as React.CSSProperties}>
        {receipt.isLoading ? (
          <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
        ) : !r ? (
          <p style={{ fontSize: 14, color: 'var(--danger)' }}>{t('cash.receiptModalLoadError')}</p>
        ) : (
          <>
            {r.voidOfReceiptId ? (
              <div
                style={{
                  background: 'var(--bg-warning-float, #FEF3E0)',
                  color: 'var(--warning)',
                  borderRadius: 'var(--r-input)',
                  padding: '10px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                {t('cash.receiptModalVoidNotice', {
                  no: r.voidOf ? `MKB-${String(r.voidOf.no).padStart(4, '0')}` : '—',
                })}
              </div>
            ) : null}

            <div
              style={{
                fontFamily: 'var(--font-mono, monospace)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '18px 20px',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>{tenant.data?.name ?? '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {[tenant.data?.settings.depotAddress, tenant.data?.settings.depotPhone]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </div>
              <Divider />
              <ReceiptRow label={t('cash.colReceiptNo')} value={r.displayNo} />
              <ReceiptRow
                label={t('cash.receiptModalDate')}
                value={`${formatDate(r.issuedAt)} ${formatTime(r.issuedAt)}`}
              />
              <ReceiptRow
                label={t('cash.receiptModalChannel')}
                value={
                  r.order
                    ? t(`orderChannel.${r.order.channel}` as Parameters<typeof t>[0])
                    : t('orderChannel.PHONE')
                }
              />
              <Divider />
              <div style={{ fontSize: 13 }}>
                {r.customer?.fullName ?? r.order?.customerName ?? '—'}
              </div>
              {(r.customer?.phone ?? r.order?.customerPhone) ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {r.customer?.phone ?? r.order?.customerPhone}
                </div>
              ) : null}
              {(r.customer?.address ?? r.order?.address) ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {r.customer?.address ?? r.order?.address}
                </div>
              ) : null}
              <Divider />
              {lines.map((l, i) => (
                <ReceiptRow key={i} label={l.label} value={formatMoney(l.amount, false)} />
              ))}
              <Divider />
              <ReceiptRow
                label={t('cash.receiptModalTotal')}
                value={formatMoney(r.amount, false)}
                bold
              />
              <ReceiptRow label={t('cash.receiptModalPayment')} value={payment} />
              <Divider />
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {t('cash.receiptModalDisclaimer')}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6 }}>AquaBox</div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <ReceiptPdfButton receiptId={r.id} />
              <button className="btn" onClick={onClose} style={{ flex: 1 }}>
                {t('common.close')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReceiptRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '3px 0',
        fontSize: 13,
        fontWeight: bold ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        borderTop: '1px dashed var(--border)',
        margin: '10px 0',
      }}
    />
  );
}

/** Aynı `PdfButton` deseni (`Cash.tsx`); modal içinde ayrı boyut/metinle. */
function ReceiptPdfButton({ receiptId }: { receiptId: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function open(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      const url = await api.getBlobUrl(`/api/receipts/${receiptId}/pdf`);
      window.open(url, '_blank', 'noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      className="btn btn-primary"
      style={{ flex: 1 }}
      onClick={() => {
        void open();
      }}
      disabled={busy}
    >
      <Icon name="list" size={14} /> {failed ? t('error.internal') : t('cash.receiptModalDownload')}
    </button>
  );
}
